/**
 * ForgotPasswordForm Component
 * 
 * A reusable forgot password form component that collects the user's
 * email and triggers a password reset flow.
 * 
 * @example
 * ```typescript
 * const forgotForm = new ForgotPasswordForm(container, {
 *   onSuccess: (email) => {
 *     console.log('Reset email sent to:', email);
 *   },
 *   onSignIn: () => showLoginForm()
 * });
 * await forgotForm.initialize();
 * ```
 */

import { IComponent } from '../IComponent';
import { ForgotPasswordFormOptions } from './AuthTypes';
import { ToasterService } from '@/services/ToasterService';
import { oemConfig } from '@/services/OEMConfig';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('ForgotPasswordForm');

export class ForgotPasswordForm implements IComponent {
  private container: HTMLElement;
  private options: ForgotPasswordFormOptions;

  constructor(container: HTMLElement, options: ForgotPasswordFormOptions) {
    this.container = container;
    this.options = options;
  }

  async initialize(): Promise<void> {
    ToasterService.init();
    this.render();
    this.bindEvents();
    logger.debug('ForgotPasswordForm initialized');
  }

  destroy(): void {
    this.container.innerHTML = '';
    logger.debug('ForgotPasswordForm destroyed');
  }

  /**
   * Render the forgot password form HTML
   */
  private render(): void {
    const oem = {
      logo: oemConfig.getSplashImage(),
      caption: oemConfig.getBrandName()
    };

    this.container.innerHTML = `
      <div class="forgot-password-form-container w-full max-w-md">
        <div class="text-center mb-8">
          <img src="${oem.logo}" alt="${oem.caption}" class="h-16 mx-auto mb-4">
          <p class="text-base-content/70 mt-2">We'll send you a reset link</p>
        </div>

        <form id="forgot-password-form-component" class="space-y-4">
          <!-- Email -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Email Address</span>
            </label>
            <input 
              type="email" 
              id="forgot-email-input"
              name="email" 
              autocomplete="email"
              placeholder="Enter your registered email" 
              class="input input-bordered w-full" 
              required 
            />
            <label class="label">
              <span class="label-text-alt text-base-content/70">
                Enter the email address associated with your account
              </span>
            </label>
          </div>

          <!-- Submit Button -->
          <button type="submit" id="forgot-submit-btn" class="btn btn-warning w-full">
            <i class="fas fa-paper-plane"></i>
            Send Reset Link
          </button>

          ${this.options.onSignIn ? `
            <!-- Links -->
            <div class="text-center text-sm mt-4">
              <p class="text-base-content/70">
                Remember your password? <a role="button" id="forgot-signin-link" class="link link-primary cursor-pointer">Sign in</a>
              </p>
            </div>
          ` : ''}
        </form>
      </div>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    const form = this.container.querySelector('#forgot-password-form-component') as HTMLFormElement;
    const signInLink = this.container.querySelector('#forgot-signin-link');

    // Form submission
    form?.addEventListener('submit', (e) => this.handleSubmit(e));

    // Sign in link
    signInLink?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.options.onSignIn?.();
    });
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const email = formData.get('email') as string;

    // Validate email
    if (!email) {
      ToasterService.warning('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      ToasterService.warning('Please enter a valid email address');
      return;
    }

    const submitBtn = this.container.querySelector('#forgot-submit-btn') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Sending...';

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      ToasterService.success('Password reset link sent to your email');
      this.options.onSuccess(email);
    } catch (error) {
      logger.error('Failed to send reset email:', error);
      ToasterService.error('Failed to send reset email. Please try again.');
      this.options.onError?.(error as Error);
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  }
}
